/**
 * Sprint M5.42 (30.07.2026) — Block A3.
 *
 * KI-Credit-Counter — sichtbar im Pro-Cockpit neben Plan-Badge.
 * Zeigt Balance / Max + Reset-Datum. Bei 0: dezenter Hinweis.
 */
import React, { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { api } from "@/lib/api";

function formatResetDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long" });
  } catch (_) {
    return null;
  }
}

export default function CreditCounter({ refreshKey = 0 }) {
  const [balance, setBalance] = useState(null);
  const [max, setMax] = useState(null);
  const [resetAt, setResetAt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/credits/balance").then(
      ({ data }) => {
        if (cancelled) return;
        setBalance(data.balance);
        setMax(data.max);
        setResetAt(data.reset_at);
        setVisible(true);
      },
      () => {
        // 404 = Free-User, kein Counter anzeigen
        setVisible(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (!visible || balance === null) return null;

  const isEmpty = balance <= 0;
  const isLow = !isEmpty && balance <= Math.max(5, Math.floor(max * 0.1));
  const resetLabel = formatResetDate(resetAt);

  return (
    <span
      data-testid="credit-counter"
      data-empty={isEmpty ? "1" : "0"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
        isEmpty
          ? "bg-slate-100 text-slate-500"
          : isLow
          ? "bg-amber-100 text-amber-800"
          : "bg-blue-100 text-[#4563BB]"
      }`}
      title={
        isEmpty
          ? `Keine Credits mehr — Reset am ${resetLabel || "1. nächsten Monats"}`
          : `${balance} von ${max} Credits — Reset am ${resetLabel || "1. nächsten Monats"}`
      }
    >
      <Zap className="w-3 h-3" />
      <span data-testid="credit-counter-value">{balance}</span>
      <span className="text-[10px] opacity-70">/ {max}</span>
    </span>
  );
}
